import React from 'react';
import Icon from '@/components/ui/AppIcon';

interface RatingCategory {
  name: string;
  score: number;
}

interface RatingSummaryProps {
  overallRating: number;
  totalReviews: number;
  ratingDistribution: {
    5: number;
    4: number;
    3: number;
    2: number;
    1: number;
  };
  categories: RatingCategory[];
}

const RatingSummary = ({
  overallRating,
  totalReviews,
  ratingDistribution,
  categories,
}: RatingSummaryProps) => {
  const getPercentage = (count: number) => {
    return (count / totalReviews) * 100;
  };

  return (
    <div className="rounded-xl bg-card p-8 shadow-warm">
      <h3 className="mb-6 font-heading text-2xl font-semibold text-foreground">
        Customer Ratings
      </h3>

      {/* Overall Rating */}
      <div className="mb-8 text-center">
        <div className="mb-3 text-5xl font-bold text-foreground">
          {overallRating.toFixed(1)}
        </div>
        <div className="mb-2 flex justify-center gap-1">
          {[...Array(5)].map((_, index) => (
            <Icon
              key={index}
              name="StarIcon"
              size={24}
              variant="solid"
              className={
                index < Math.floor(overallRating)
                  ? 'text-warning' :'text-muted-foreground/30'
              }
            />
          ))}
        </div>
        <p className="text-muted-foreground">
          Based on {totalReviews} reviews
        </p>
      </div>

      {/* Rating Distribution */}
      <div className="mb-8 space-y-3">
        {[5, 4, 3, 2, 1].map((rating) => {
          const count = ratingDistribution[rating as keyof typeof ratingDistribution];
          const percentage = getPercentage(count);

          return (
            <div key={rating} className="flex items-center gap-3">
              <div className="flex w-12 items-center gap-1">
                <span className="text-sm font-medium text-foreground">
                  {rating}
                </span>
                <Icon
                  name="StarIcon"
                  size={14}
                  variant="solid"
                  className="text-warning"
                />
              </div>
              <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-warning transition-smooth"
                  style={{ width: `${percentage}%` }}
                />
              </div>
              <span className="w-12 text-right text-sm text-muted-foreground">
                {count}
              </span>
            </div>
          );
        })}
      </div>

      {/* Category Ratings */}
      <div className="space-y-4 border-t border-border pt-6">
        <h4 className="font-heading text-lg font-semibold text-foreground">
          Rating Breakdown
        </h4>
        {categories.map((category, index) => (
          <div key={index}>
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-medium text-foreground">
                {category.name}
              </span>
              <span className="text-sm font-medium text-foreground">
                {category.score.toFixed(1)}
              </span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-primary transition-smooth"
                style={{ width: `${(category.score / 5) * 100}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RatingSummary;